import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "./context/AuthContext";
import Layout from "./components/Layout";
import Home from "./pages/Home";
import SoftwareList from "./pages/SoftwareList";
import SoftwareDetail from "./pages/SoftwareDetail";
import BestOf from "./pages/BestOf";
import Comparisons from "./pages/Comparisons";
import ComparisonDetail from "./pages/ComparisonDetail";
import Guides from "./pages/Guides";
import ArticleDetail from "./pages/ArticleDetail";
import CategoryPage from "./pages/CategoryPage";
import SearchResults from "./pages/SearchResults";
import Tools from "./pages/Tools";
import RouteCalculator from "./pages/tools/RouteCalculator";
import CapacityEstimator from "./pages/tools/CapacityEstimator";
import IntegrationMatrix from "./pages/tools/IntegrationMatrix";
import { About, Contact, Methodology } from "./pages/TrustPages";
import AdminLogin from "./pages/admin/AdminLogin";
import AdminLayout from "./pages/admin/AdminLayout";
import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminSoftware from "./pages/admin/AdminSoftware";
import AdminArticles from "./pages/admin/AdminArticles";
import AdminComparisons from "./pages/admin/AdminComparisons";
import AdminCategories from "./pages/admin/AdminCategories";
import AdminMedia from "./pages/admin/AdminMedia";
import KeywordLanding from "./pages/KeywordLanding";
import { Link } from "react-router-dom";
import { useSEO } from "./lib/seo";

function NotFound() {
  useSEO({ title: "Page Not Found (404)", description: "The page you're looking for doesn't exist.", noindex: true });
  return (
    <div className="max-w-3xl mx-auto px-4 py-32 text-center">
      <p className="font-heading text-6xl font-black text-sky-500">404</p>
      <h1 className="font-heading text-2xl font-bold text-slate-900 mt-4">Page not found</h1>
      <p className="text-slate-500 mt-2">The page you're looking for doesn't exist or was moved.</p>
      <div className="mt-8 flex flex-wrap gap-3 justify-center">
        <Link to="/" className="px-5 py-2.5 rounded-full bg-slate-900 text-white text-sm font-semibold hover:bg-slate-700 transition-colors duration-200">Go home</Link>
        <Link to="/software" className="px-5 py-2.5 rounded-full border border-border bg-white text-slate-700 text-sm font-semibold hover:border-sky-400 hover:text-sky-600 transition-colors duration-200">Browse reviews</Link>
      </div>
    </div>
  );
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route element={<Layout />}>
              <Route path="/" element={<Home />} />
              <Route path="/software" element={<SoftwareList />} />
              <Route path="/software/:slug" element={<SoftwareDetail />} />
              <Route path="/best-pool-service-software" element={<BestOf />} />
              <Route path="/comparisons" element={<Comparisons />} />
              <Route path="/compare/:slug" element={<ComparisonDetail />} />
              <Route path="/guides" element={<Guides />} />
              <Route path="/guides/:slug" element={<ArticleDetail />} />
              <Route path="/category/:slug" element={<CategoryPage />} />
              <Route path="/search" element={<SearchResults />} />
              <Route path="/tools" element={<Tools />} />
              <Route path="/tools/route-profitability-calculator" element={<RouteCalculator />} />
              <Route path="/tools/technician-capacity-estimator" element={<CapacityEstimator />} />
              <Route path="/tools/integration-matrix" element={<IntegrationMatrix />} />
              <Route path="/about" element={<About />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/methodology" element={<Methodology />} />
              <Route path="/pool-and-spa-service-software" element={<KeywordLanding slug="pool-and-spa-service-software" />} />
              <Route path="/pool-maintenance-software" element={<KeywordLanding slug="pool-maintenance-software" />} />
              <Route path="*" element={<NotFound />} />
            </Route>
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin" element={<AdminLayout />}>
              <Route index element={<AdminDashboard />} />
              <Route path="software" element={<AdminSoftware />} />
              <Route path="articles" element={<AdminArticles />} />
              <Route path="comparisons" element={<AdminComparisons />} />
              <Route path="categories" element={<AdminCategories />} />
              <Route path="media" element={<AdminMedia />} />
            </Route>
          </Routes>
        </BrowserRouter>
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </div>
  );
}

export default App;
